import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { FileText, Download, FileSignature, MessageSquare, Check, Eye, X } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import { getFileUrl, isImage, isPdf } from '@/utils/fileUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WorkerDocumenti() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/my-documents`, { withCredentials: true });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Errore nel caricamento documenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const markAsRead = async (docId) => {
    try {
      await axios.put(`${API}/my-documents/${docId}/read`, {}, { withCredentials: true });
      setDocuments(prev => prev.map(d => d.id === docId ? { ...d, read: true } : d));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // iter44: conferma esplicita di lettura (separata dal "visto" automatico all'apertura)
  const confirmRead = async (docId) => {
    try {
      const r = await axios.put(`${API}/my-documents/${docId}/confirm-read`, {}, { withCredentials: true });
      setDocuments(prev => prev.map(d => d.id === docId
        ? { ...d, read: true, read_confirmed: true, read_confirmed_at: r.data?.read_confirmed_at }
        : d,
      ));
      toast.success('Lettura confermata');
    } catch (error) {
      toast.error('Errore conferma lettura');
    }
  };

  const handleOpen = (doc) => {
    if (!doc.file_url) {
      toast.error('File non disponibile');
      return;
    }
    if (!doc.read) markAsRead(doc.id);
    setPreviewDoc(doc);
    setPreviewOpen(true);
  };

  const handleDownload = async (doc) => {
    if (!doc.file_url) { toast.error('File non disponibile'); return; }
    try {
      const response = await axios.get(`${API}/files/${doc.file_url}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = doc.file_type?.split('/')?.pop() || 'pdf';
      link.download = `${doc.document_title || 'documento'}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      if (!doc.read) markAsRead(doc.id);
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  const handleSign = async (docId) => {
    try {
      await axios.put(`${API}/my-documents/${docId}/sign`, { signature_data: "confirmed" }, { withCredentials: true });
      toast.success('Documento firmato per ricezione');
      setDocuments(prev => prev.map(d => d.id === docId ? { ...d, signed: true } : d));
    } catch { toast.error('Errore'); }
  };

  const unreadCount = documents.filter(d => !d.read).length;
  const unconfirmedCount = documents.filter(d => !d.read_confirmed).length;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-4" data-testid="worker-documenti">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-xl font-bold text-slate-900">Documenti</h1>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Badge className="bg-blue-600 text-white" data-testid="unread-count">
              {unreadCount} nuov{unreadCount === 1 ? 'o' : 'i'}
            </Badge>
          )}
          {unconfirmedCount > 0 && (
            <Badge className="bg-amber-500 text-white" data-testid="unconfirmed-count">
              {unconfirmedCount} da confermare
            </Badge>
          )}
        </div>
      </div>

      {documents.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Nessun documento ricevuto</p>
            <p className="text-xs text-slate-400 mt-1">I documenti inviati dall'admin appariranno qui</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <Card
              key={doc.id}
              className={`border-slate-200 transition-all ${!doc.read ? 'border-l-4 border-l-blue-500 bg-blue-50/30' : ''}`}
              data-testid={`document-card-${doc.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${!doc.read ? 'bg-blue-100' : 'bg-slate-100'}`}>
                    <FileText className={`h-5 w-5 ${!doc.read ? 'text-blue-600' : 'text-slate-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-slate-900 truncate">{doc.document_title}</h3>
                      {!doc.read && (
                        <span className="flex-shrink-0 h-2 w-2 rounded-full bg-blue-500"></span>
                      )}
                      {doc.requires_signature && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs flex-shrink-0">
                          <FileSignature className="h-3 w-3 mr-1" />
                          Firma
                        </Badge>
                      )}
                    </div>
                    {doc.document_description && (
                      <p className="text-sm text-slate-500 mb-1 line-clamp-2">{doc.document_description}</p>
                    )}
                    {doc.message && (
                      <div className="flex items-start gap-1.5 mt-2 p-2 bg-slate-50 rounded-md border border-slate-200">
                        <MessageSquare className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-slate-600">{doc.message}</p>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
                      <p className="text-xs text-slate-400">
                        Ricevuto il {format(new Date(doc.created_at), 'dd MMM yyyy, HH:mm', { locale: it })}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* iter44: bottone "Conferma lettura" persistente finché non cliccato */}
                        {!doc.read_confirmed && (
                          <Button
                            variant="default"
                            size="sm"
                            className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={() => confirmRead(doc.id)}
                            data-testid={`confirm-read-${doc.id}`}
                          >
                            <Check className="h-3.5 w-3.5 mr-1.5" />Conferma lettura
                          </Button>
                        )}
                        {doc.read_confirmed && (
                          <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium" data-testid={`confirmed-${doc.id}`}>
                            <Check className="h-3.5 w-3.5" />
                            Letto {doc.read_confirmed_at ? `il ${format(new Date(doc.read_confirmed_at), 'dd MMM yyyy, HH:mm', { locale: it })}` : ''}
                          </span>
                        )}
                        {doc.requires_signature && !doc.signed && (
                          <Button variant="outline" size="sm" className="h-8 text-xs text-amber-700 border-amber-300" onClick={() => handleSign(doc.id)} data-testid={`sign-doc-${doc.id}`}>
                            <FileSignature className="h-3.5 w-3.5 mr-1.5" />Firma ricezione
                          </Button>
                        )}
                        {doc.signed && (
                          <span className="flex items-center gap-1 text-xs text-emerald-600"><Check className="h-3.5 w-3.5" />Firmato</span>
                        )}
                        <Button variant="default" size="sm" onClick={() => handleOpen(doc)} className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid={`open-doc-${doc.id}`}>
                          <Eye className="h-3.5 w-3.5 mr-1.5" />Apri
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDownload(doc)} className="h-8 text-xs" data-testid={`download-doc-${doc.id}`}>
                          <Download className="h-3.5 w-3.5 mr-1.5" />Scarica
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Dialog with prominent Close button for mobile */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-full sm:max-w-[95vw] w-screen sm:w-auto h-[100dvh] sm:h-[90vh] sm:max-h-[90vh] p-0 gap-0 rounded-none sm:rounded-lg flex flex-col">
          <div className="flex items-center justify-between gap-2 px-4 pt-[max(env(safe-area-inset-top),0.75rem)] pb-3 border-b bg-white">
            <div className="min-w-0 flex-1">
              <h2 className="font-semibold text-slate-900 truncate text-sm sm:text-base">{previewDoc?.document_title || 'Documento'}</h2>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPreviewOpen(false)}
              className="h-9 px-3 -mr-2 text-slate-700 hover:bg-slate-100 shrink-0"
              data-testid="close-preview-btn"
            >
              <X className="h-4 w-4 mr-1" />
              <span className="text-sm font-medium">Chiudi</span>
            </Button>
          </div>
          <div className="flex-1 min-h-0 bg-slate-100 overflow-auto">
            {(() => {
              if (!previewDoc?.file_url) return null;
              if (isImage(previewDoc.file_url)) {
                return <img src={getFileUrl(previewDoc.file_url)} alt={previewDoc.document_title} className="max-w-full h-auto mx-auto block" />;
              }
              if (isPdf(previewDoc.file_url)) {
                return <iframe src={getFileUrl(previewDoc.file_url)} className="w-full h-full border-0" title="Anteprima documento" />;
              }
              return (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <FileText className="h-16 w-16 text-slate-300 mb-4" />
                  <p className="text-slate-600 mb-2">Anteprima non disponibile per questo formato</p>
                  <Button onClick={() => previewDoc && handleDownload(previewDoc)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="download-from-preview">
                    <Download className="h-4 w-4 mr-2" />Scarica per visualizzare
                  </Button>
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-2 px-4 py-3 border-t bg-white pb-[max(env(safe-area-inset-bottom),0.75rem)]">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => previewDoc && handleDownload(previewDoc)}
              data-testid="download-from-preview-bottom"
            >
              <Download className="h-4 w-4 mr-2" />Scarica
            </Button>
            {previewDoc && !previewDoc.read_confirmed && (
              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => { confirmRead(previewDoc.id); setPreviewOpen(false); }}
                data-testid="confirm-from-preview"
              >
                <Check className="h-4 w-4 mr-2" />Conferma lettura
              </Button>
            )}
            {previewDoc?.requires_signature && !previewDoc?.signed && (
              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => { handleSign(previewDoc.id); setPreviewOpen(false); }}
                data-testid="sign-from-preview"
              >
                <FileSignature className="h-4 w-4 mr-2" />Firma
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
